import { MapTrifoldIcon, NotebookIcon } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';

import { EmptyState } from '@/components/empty-state';
import { ICON_WEIGHT } from '@/components/icon';
import { StatusBadge } from '@/components/status-badge';
import {
  airTimeMinutes,
  type FlightSummary,
  routeLabel,
  summaryLogbookStatus,
  useAircraftFlightSummaries,
} from '@/features/flights';
import { formatDecimalHours } from '@/features/logbook/time';
import { confirmedHoursTotal } from '@/features/logbook/totals';
import { DEFAULT_SETTINGS, useSettings } from '@/features/settings';
import { formatCalendarDate, formatCount, formatLocalTime } from '@/lib/format';
import { convertDistance, DISTANCE_UNIT_LABEL } from '@/lib/units';

/**
 * Everything flown in one aeroplane — `PLAN.md` §6 Phase 4's aircraft register,
 * finally reading back out.
 *
 * The register was write-only: a page for defining an aircraft and nothing
 * that said what had been flown in it. This is the other half. Tracked flights
 * and logbook entries appear in the same list because they are the same
 * events; a flight with no track still belongs on the aircraft that flew it,
 * and a track imported before the aircraft was on file still counts as having
 * happened.
 *
 * Only approved entries go into the hours total, for the same reason the
 * logbook's own totals ignore drafts — `NZ-LOGBOOK.md` §2.3. Drafts are
 * counted separately and said out loud, so the number never quietly disagrees
 * with the logbook.
 */

function Stat({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div className="border-border bg-surface-sunken rounded-container border px-3 py-2">
      <dt className="text-muted-foreground text-[0.625rem] font-medium tracking-wider uppercase">
        {label}
      </dt>
      <dd className="text-foreground mt-0.5 text-lg leading-tight font-semibold">
        <span className="figure">{value}</span>
        {unit === undefined ? null : (
          <span className="text-muted-foreground ml-1 text-xs font-normal">{unit}</span>
        )}
      </dd>
    </div>
  );
}

function FlightRow({
  summary,
  dateFormat,
  timeZone,
}: {
  summary: FlightSummary;
  dateFormat: 'nz' | 'iso';
  timeZone: string;
}) {
  const status = summaryLogbookStatus(summary);
  const route = routeLabel(summary);
  const airMin = airTimeMinutes(summary);

  return (
    <li className="border-border flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b py-2 last:border-0">
      <span className="text-foreground w-28 text-sm">
        {formatCalendarDate(summary.date, { format: dateFormat })}
      </span>
      {summary.startTime === undefined ? null : (
        <span className="text-muted-foreground tabular text-xs">
          {formatLocalTime(summary.startTime, { timeZone })}
        </span>
      )}
      <span className="text-foreground min-w-0 flex-1 truncate text-sm">{route ?? '—'}</span>
      <span className="text-muted-foreground tabular text-xs">
        {airMin === undefined ? '—' : `${formatDecimalHours(airMin / 60)} hr`}
      </span>
      <StatusBadge status={status} />
      <span className="flex items-center gap-2">
        {summary.track === undefined ? null : (
          <Link
            to="/flights/$trackId"
            params={{ trackId: summary.track.id }}
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs underline-offset-2 hover:underline"
          >
            <MapTrifoldIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-3.5" />
            Map
          </Link>
        )}
        {summary.entry === undefined ? null : (
          <Link
            to="/logbook"
            hash={summary.entry.id}
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs underline-offset-2 hover:underline"
          >
            <NotebookIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-3.5" />
            Logbook
          </Link>
        )}
      </span>
    </li>
  );
}

export interface AircraftFlightsProps {
  aircraftId: string;
}

export function AircraftFlights({ aircraftId }: AircraftFlightsProps) {
  const summaries = useAircraftFlightSummaries(aircraftId);
  const settings = useSettings() ?? DEFAULT_SETTINGS;

  if (summaries === null) {
    return (
      <p className="text-muted-foreground text-sm" data-testid="aircraft-flights-loading">
        Opening the local database…
      </p>
    );
  }

  if (summaries.length === 0) {
    return (
      <EmptyState
        icon={MapTrifoldIcon}
        title="No flights"
        description="Import a track or add a logbook entry for this aircraft."
      />
    );
  }

  /*
   * `confirmedAt`, not `!isDraft` — the same test `totals.ts` and
   * `aggregate.ts` apply, so this stat can never quietly disagree with the
   * logbook's own running total about which entries have been reviewed.
   */
  const approvedHours = confirmedHoursTotal(
    summaries.flatMap((summary) => (summary.entry === undefined ? [] : [summary.entry])),
  );
  const draftCount = summaries.filter(
    (summary) => summary.entry !== undefined && summary.entry.confirmedAt === undefined,
  ).length;
  const trackedCount = summaries.filter((summary) => summary.track !== undefined).length;
  const distanceNm = summaries.reduce(
    (total, summary) => total + (summary.stats.distanceNm ?? 0),
    0,
  );

  return (
    <div className="space-y-3" data-testid="aircraft-flights">
      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Flights" value={formatCount(summaries.length)} />
        <Stat label="Logged hours" value={formatDecimalHours(approvedHours)} unit="hr" />
        <Stat label="Tracked" value={formatCount(trackedCount)} />
        <Stat
          label="Distance"
          value={formatCount(Math.round(convertDistance(distanceNm, settings.distanceUnit)))}
          unit={DISTANCE_UNIT_LABEL[settings.distanceUnit]}
        />
      </dl>
      {draftCount === 0 ? null : (
        <p className="text-muted-foreground text-xs">
          {formatCount(draftCount)} {draftCount === 1 ? 'entry' : 'entries'} not yet approved. Hours
          excluded from the total above.
        </p>
      )}
      <ul className="border-border rounded-container border px-3">
        {summaries.map((summary) => (
          <FlightRow
            key={summary.id}
            summary={summary}
            dateFormat={settings.dateFormat}
            timeZone={settings.timeZone}
          />
        ))}
      </ul>
    </div>
  );
}
